import { useContext, useEffect, useEffectEvent, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Box, Button, Checkbox, CircularProgress, DialogContent, FormControlLabel, Grid, Step, StepLabel, Stepper, Typography } from '@mui/material'
import { differenceInMinutes } from 'date-fns'
import { Form, Formik, FormikErrors } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import { AppContext, useHost } from '../../providers/app-provider'
import { getDevices } from '../../routes/accounts/components/devices-tab-panel/helpers'
import { DeviceTableRow } from '../../routes/accounts/components/devices-tab-panel/types'
import DeviceContext from '../../routes/devices/context'
import Api from '../../utils/api'
import { getFormattedDateTime, locationsListArray, showDeviceMenuItems } from '../../utils/helpers'
import useLoadDevicePhoto from '../../utils/hooks/useLoadDevicePhoto'
import useObjectUrlCleanup from '../../utils/hooks/useObjectUrlCleanup'
import useWebsocket from '../../utils/hooks/useWebsocket'
import useSharedStyles from '../../utils/shared.styles'
import DeviceEditFields from '../device-edit-modal/device-edit-fields'
import DevicePhotoModal from '../device-photo-modal'
import DialogWithTitle from '../dialog-with-title'
import FormikDropdown from '../formik-dropdown'
import StatusChip from '../status-chip'

import ActivationTypeChip from './activation-type-chip'
import DeviceInfo from './device-info'
import DevicePhotoThumbnail from './device-photo-thumbnail'
import useStyles from './index.styles'

interface DeviceActivateModalProps {
  device: DeviceTableRow | IDeviceAPIObject
  deactivatedDevices: DeviceTableRow[] | undefined
  open: boolean
  onClose: () => void
  onContinue: () => void
  folderTheme: IFolderTheme | undefined
}

const steps = ['Activation Type', 'Device Details', 'Review']

const DeviceActivateModal = ({ device, deactivatedDevices: deactivatedDevicesProp, open, onClose, onContinue, folderTheme }: DeviceActivateModalProps) => {
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const { enqueueSnackbar } = useSnackbar()
  const auth = useAuth()
  const host = useHost()
  const { pageRefreshCounter } = useContext(AppContext)

  const [deactivatedDevices, setDeactivatedDevices] = useState<DeviceTableRow[] | IDeviceAPIObject[]>(deactivatedDevicesProp || [])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [activeStep, setActiveStep] = useState<number>(0)
  const [deviceStatus, setDeviceStatus] = useState<string>(
    differenceInMinutes(new Date(), new Date(device.lastCheckIn || 0)) <= 15 ? 'Online' : device.deviceStatus
  ) // Device is considered online if it has checked in in the last 15 minutes
  const [lastCheckIn, setLastCheckIn] = useState<string | null>(device.lastCheckIn)

  const { setDevicePhotoRefreshCounter } = useContext(DeviceContext)

  const [hasPhoto, setHasPhoto] = useState<boolean>(device?.hasPhoto || false)
  const [showDevicePhotoModal, setShowDevicePhotoModal] = useState<boolean>(false)
  const shouldLoad = !!device?.hasPhoto
  const { photoPath, setPhotoPath } = useLoadDevicePhoto(device?.id, shouldLoad, 'thumb')

  // Cleanup object URL when component unmounts or photoPath changes
  useObjectUrlCleanup(photoPath)

  const { resourcesLink } = showDeviceMenuItems(device)

  const initialValues: IFormValues = {
    activationType: 'new',
    oldDeviceId: '',
    shouldCopyLocationInfo: true,
    useCase: '',
    location: '',
    notes: '',
    building: '',
    floor: '',
  }

  const validationSchema = Yup.object().shape({
    useCase: Yup.string().trim().required('Required'),
    location: Yup.string().trim().required('Required'),
    floor: Yup.string().max(4, 'Floor must be four or fewer characters'),
    notes: Yup.string().trim().required('Required'),
  })

  // Listen to websocket messages to update device status and last check-in
  const onWebsocketMessage = (message: WebsocketDataType['payload']) => {
    setDeviceStatus('Online') // If we get a message from the device, we assume it is now online
    setLastCheckIn(new Date(message.timestamp * 1000).toISOString()) // Convert timestamp to milliseconds
  }
  useWebsocket({ subscribeIds: [device.id], onMessage: onWebsocketMessage })

  const getFolderDevices = useEffectEvent(async () => {
    if (!host || !auth.user?.access_token) return
    setIsLoading(true)
    const res = await getDevices(device.folderId, host)
    if (res.ok) {
      if (res?.devices?.length > 0) {
        setDeactivatedDevices(res.devices.filter((d) => d.deviceStatus === 'Deactivated'))
      }
    } else {
      enqueueSnackbar('Something went wrong, and list of deactivated devices did not load', { variant: 'error' })
    }
    setIsLoading(false)
  })

  useEffect(() => {
    if (!deactivatedDevicesProp) {
      getFolderDevices()
    }
  }, [deactivatedDevicesProp, device.folderId, host, pageRefreshCounter])

  const handleNext = async (
    values: IFormValues,
    setFieldValue: (field: string, value: string, shouldValidate?: boolean) => Promise<void | FormikErrors<IFormValues>>,
    oldDevice: DeviceTableRow | IDeviceAPIObject | undefined
  ) => {
    if (values.activationType === 'replacement' && values.shouldCopyLocationInfo && oldDevice && activeStep === 0) {
      const newLocation = oldDevice.area || oldDevice.metadata?.device_location || ''
      const locationsList = locationsListArray(folderTheme)

      await setFieldValue(
        'useCase',
        oldDevice.deviceUsecase && device.nodeType.allowedUsecases.includes(oldDevice.deviceUsecase) ? oldDevice.deviceUsecase : ''
      )
      await setFieldValue('location', newLocation && locationsList.includes(newLocation) ? newLocation : '')
      await setFieldValue('notes', oldDevice.metadata?.device_location_note || '')
      await setFieldValue('building', oldDevice.building)
      await setFieldValue('floor', oldDevice.floor)
    }
    if (values.activationType === 'new' && activeStep === 0) {
      await setFieldValue('useCase', '')
      await setFieldValue('location', '')
      await setFieldValue('notes', '')
      await setFieldValue('building', '')
      await setFieldValue('floor', '')
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const onSubmit = async (values: IFormValues) => {
    const res = await Api.patch(`${host}/api/v2/central/devices/${device.id}/usecaselocation`, {
      building: values.building,
      emptyusecases: false,
      floor: values.floor,
      location: values.location,
      notes: values.notes,
      usecases: [values.useCase],
      isActivated: true,
    })
    if (res.ok) {
      onContinue()
      onClose()
      enqueueSnackbar(`Device activated successfully`)

      Api.patch(`${host}/api/v2/folders/${device.folderId}/attributes`, {
        activationApp: 'Central',
      })
    } else {
      enqueueSnackbar(`Could not activate device.`, { variant: 'error' })
    }
  }

  const onSavePhoto = (uploadedPhoto: string) => {
    setPhotoPath(uploadedPhoto)
    setHasPhoto(true)

    setDevicePhotoRefreshCounter((prev) => prev + 1)

    setShowDevicePhotoModal(false)
    enqueueSnackbar('Device photo updated successfully.', { variant: 'success' })
  }

  const handleShowDevicePhotoModal = () => {
    setShowDevicePhotoModal(true)
  }

  const deactivatedList = deactivatedDevices.map((d) => ({ id: String(d.id), name: d.vanity }))

  return (
    <DialogWithTitle open={open} title="Activate Device" setOpen={onClose} fullWidth>
      <DialogContent>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => {
            const stepProps: { completed?: boolean } = {}
            return (
              <Step key={label} {...stepProps}>
                <StepLabel>{label}</StepLabel>
              </Step>
            )
          })}
        </Stepper>

        <Formik<IFormValues> initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
          {({ isValid, isSubmitting, values, setFieldValue, isValidating }) => {
            const { activationType, oldDeviceId, shouldCopyLocationInfo, useCase, location, building, floor, notes } = values
            const oldDevice = deactivatedDevices.find((d) => String(d.id) === values.oldDeviceId)

            return (
              <Form>
                <Grid
                  container={true}
                  size={12}
                  sx={{
                    flexDirection: 'column',
                    gap: 2,
                    marginTop: 2,
                    display: activeStep === 0 ? 'flex' : 'none',
                  }}
                >
                  <DeviceInfo title="Activating:" vanity={device.vanity} uniqueId={device.uniqueId} firmwareVersion={device.firmwareVersion} />

                  <Grid>
                    <Typography variant="subtitle2">This is a:</Typography>
                    <Grid
                      container={true}
                      sx={{
                        gap: 3,
                        marginTop: '8px',
                      }}
                    >
                      <ActivationTypeChip label="Replacement" value="replacement" />
                      <ActivationTypeChip label="New Device" value="new" />
                    </Grid>
                    <Grid
                      sx={{
                        maxWidth: '361px',
                        marginTop: '8px',
                      }}
                    >
                      <FormikDropdown
                        name="oldDeviceId"
                        label="Select the old device"
                        required={activationType === 'replacement'}
                        disabled={activationType === 'new' || isLoading}
                        items={deactivatedList}
                        size="medium"
                      />
                    </Grid>

                    <FormControlLabel
                      control={<Checkbox checked={shouldCopyLocationInfo} />}
                      onChange={(_e, checked) => setFieldValue('shouldCopyLocationInfo', checked)}
                      disabled={activationType === 'new'}
                      label="Copy the location info to new device"
                    />
                  </Grid>
                </Grid>
                <Grid
                  container={true}
                  size={12}
                  sx={{
                    flexDirection: 'column',
                    marginTop: 2,
                    display: activeStep === 1 ? 'flex' : 'none',
                  }}
                >
                  <DeviceInfo
                    title="Activating:"
                    vanity={device.vanity}
                    uniqueId={device.uniqueId}
                    useCase={useCase as DeviceUseCaseType | ''}
                    firmwareVersion={device.firmwareVersion}
                  />

                  <DevicePhotoThumbnail hasPhoto={hasPhoto} photoPath={photoPath} handleShowDevicePhotoModal={handleShowDevicePhotoModal} />

                  <Grid container={true} spacing={2}>
                    <DeviceEditFields device={device} initialValues={initialValues} folderTheme={folderTheme} />
                  </Grid>
                </Grid>
                <Grid
                  container={true}
                  size={12}
                  spacing={2}
                  sx={{
                    flexDirection: 'column',
                    marginTop: 2,
                    display: activeStep === 2 ? 'flex' : 'none',
                  }}
                >
                  <DeviceInfo
                    title="Review Device Details:"
                    vanity={device.vanity}
                    uniqueId={device.uniqueId}
                    useCase={useCase as DeviceUseCaseType | ''}
                    firmwareVersion={device.firmwareVersion}
                    resourcesLink={resourcesLink}
                  />

                  <DevicePhotoThumbnail hasPhoto={hasPhoto} photoPath={photoPath} handleShowDevicePhotoModal={handleShowDevicePhotoModal} />

                  <Grid>
                    <Typography variant="body1">{[building, `Floor ${floor}`, location].filter((x) => x).join(' • ')}</Typography>
                    <Typography variant="body1" color="textSecondary">
                      {notes}
                    </Typography>
                  </Grid>

                  <Grid>
                    <Typography variant="subtitle2">Check Connection</Typography>
                    <Grid
                      container={true}
                      sx={{
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Typography variant="overline" color="textSecondary">
                        Last Check-In
                      </Typography>
                    </Grid>

                    <Grid
                      container={true}
                      sx={{
                        justifyContent: 'space-between',
                      }}
                    >
                      <Grid container={true} sx={[sharedClasses.centerContentVertical, { gap: 1 }]}>
                        <StatusChip status={deviceStatus.toLowerCase()} label={deviceStatus} />
                        {deviceStatus !== 'Online' && <CircularProgress size="16px" sx={classes.statusWaitingIcon} />}
                      </Grid>
                      <Typography variant="body1">{lastCheckIn ? getFormattedDateTime(lastCheckIn) : 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid
                  container={true}
                  sx={{
                    paddingTop: '16px',
                  }}
                >
                  {activeStep > 0 && <Button onClick={handleBack}>Back</Button>}

                  <Box sx={{ flex: '1 1 auto' }} />

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!isValid || deviceStatus !== 'Online' || isSubmitting}
                    loading={isSubmitting}
                    sx={{ display: activeStep === steps.length - 1 ? 'inline-flex' : 'none' }}
                  >
                    Complete
                  </Button>

                  <Button
                    type="button"
                    variant="contained"
                    disabled={(activationType === 'replacement' && !oldDeviceId) || (activeStep === 1 && !isValid) || isValidating}
                    loading={isValidating}
                    onClick={() => handleNext(values, setFieldValue, oldDevice)}
                    sx={{ display: activeStep === steps.length - 1 ? 'none' : 'inline-flex' }}
                  >
                    Continue
                  </Button>
                </Grid>
              </Form>
            )
          }}
        </Formik>
      </DialogContent>
      <DevicePhotoModal
        deviceId={device.id}
        open={showDevicePhotoModal}
        onClose={() => setShowDevicePhotoModal(false)}
        photoPath={photoPath}
        onSave={onSavePhoto}
      />
    </DialogWithTitle>
  )
}

export default DeviceActivateModal
